import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { BinderNote } from '@/models/binder.model';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { EditorModule } from 'primeng/editor';
import { CardModule } from 'primeng/card';
import { BinderNotesService } from '@/services/api/binder-notes.service';
import { RichTextEditorComponent } from '@/global/rich-text-editor.component';
import { ToolbarModule } from 'primeng/toolbar';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FluidModule } from 'primeng/fluid';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BindersService } from '@/services/api/binders.service';

@Component({
  selector: 'app-binder-notes',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ButtonModule, 
    InputTextModule, 
    EditorModule, 
    CardModule,
    RichTextEditorComponent,
    ToolbarModule,
    IconFieldModule,
    InputIconModule,
    FluidModule,
    ToggleSwitchModule
],
  template: `

  <!-- NOTE LIST VIEW -->
<div *ngIf="!editingNote" class="gap-3">
<p-toolbar>
    <ng-template #start>
        <p-button icon="pi pi-plus" class="mr-2" text severity="secondary" />
        <p-button icon="pi pi-print" class="mr-2" text severity="secondary" />
        <p-button icon="pi pi-upload" text severity="secondary" />
    </ng-template>
    <ng-template #center>
        <p-iconfield iconPosition="left">
            <p-inputicon class="pi pi-search" />
            <input type="text" pInputText placeholder="Search" />
        </p-iconfield>
    </ng-template>
    <ng-template #end>
    <button pButton label="New Note" icon="pi pi-plus" (click)="startCreate()"></button>
    </ng-template>
</p-toolbar>
  <div *ngFor="let note of noteService.notes()" class="card mt-1">
    <div class="p-card-header flex justify-content-between align-items-center">
      <strong>{{ note.Subject }}</strong>
      <span class="text-sm text-500">by {{ note.UserName }} on {{ note.CreatedOn | date:'short' }}</span>
    </div>
    <div class="p-card-body">
      <div [innerHTML]="note.Note"></div>
    </div>
    <div class="p-card-footer mt-2 flex gap-2">
      <button pButton icon="pi pi-pencil" label="Edit" (click)="startEdit(note)">
      </button>
      <button pButton icon="pi pi-trash" label="Delete" severity="danger" (click)="deleteNote(note)">
      </button>
    </div>
  </div>
</div>
<!-- NOTE EDIT/CREATE FORM VIEW -->
<p-fluid *ngIf="editingNote">
<form #noteForm="ngForm" (ngSubmit)="saveNote(noteForm)" class="flex flex-column gap-3">
            <div class="flex flex-col gap-4 w-full">
                <div class="font-semibold text-xl">{{ editingNote.BNoteID === 0 ? 'Create Note' : 'Edit Note' }}</div>
                <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex flex-wrap gap-2 w-full">
                        <label>Subject</label>
                        <input pInputText name="Subject" [(ngModel)]="editingNote.Subject" required />
                    </div>
                    <div class="flex flex-wrap flex-col gap-2 w-full">
                    <label>Drafting-Only</label>
                        <p-toggleswitch name="DraftOnly" [(ngModel)]="editingNote.DraftOnly" />
                    </div>
                </div>

                <div class="flex flex-wrap">
                <label>Note</label>
                <app-rich-text-editor name="Note" [(ngModel)]="editingNote.Note" [style]="{ height: '100%' }" class="w-full"></app-rich-text-editor>
                </div>

                <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex flex-wrap gap-2 w-full">
                    <button pButton type="submit" label="Save" icon="pi pi-save" [disabled]="!noteForm.valid">
                    </button>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <button pButton type="button" label="Cancel" icon="pi pi-times" severity="secondary" (click)="cancelEdit()">
                        </button>
                    </div>
                </div>
            </div>
        </form>
    </p-fluid>

<!-- 
<div *ngIf="editingNote">
  <h3>{{ editingNote.BNoteID === 0 ? 'Create Note' : 'Edit Note' }}</h3>

  <form #noteForm="ngForm" (ngSubmit)="saveNote(noteForm)" class="flex flex-column gap-3">
    <label>Subject</label>
    <input pInputText name="Subject" [(ngModel)]="editingNote.Subject" required />

    <label>Note</label>
    <app-rich-text-editor [(ngModel)]="editingNote.Note" [style]="{ height: '200px' }"></app-rich-text-editor>
    <div class="flex gap-2">
      <button pButton type="submit" label="Save" icon="pi pi-save" [disabled]="!noteForm.valid">
      </button>
      <button pButton type="button" label="Cancel" icon="pi pi-times" severity="secondary" (click)="cancelEdit()">
      </button>
    </div>
  </form>
</div> -->

  `,
})
export class BinderNotesComponent {
  public noteService: BinderNotesService = inject(BinderNotesService);
  binderService: BindersService = inject(BindersService);


  editingNote: Partial<BinderNote> | null = null;
  @Input() caseId: number | null = null;

  startEdit(note: BinderNote) {
    this.editingNote = { ...note }; // Shallow clone
    this.noteService.setSelectedNote(note);
  }

  get binderId() {
    return this.binderService.selectedBinder()?.BinderID;
  }

  startCreate() {
    this.editingNote = {
      BinderID: this.binderId || 0,
      Note: '',
      CreatedOn: new Date(),
      UID: 0,
      UserName: '',
      BNoteID: 0,
      Modified: new Date(),
      ModUID: 0,
      ModUserName: '',
      Subject: '',
      BinderNote: false,
      DraftOnly: true,
      DraftingNote: false,
      FileLink: '',
      EventID: 0,
      HotNote: false,
      LassoNoteID: '',
      EntryID: 0,
      CaseID: this.caseId,
      binderCaseId: null,
    };
  }

  cancelEdit() {
    this.editingNote = null;
    this.noteService.unselectNote();
  }

  saveNote(form: NgForm) {
    if (!form.valid || !this.editingNote) return;

    if (this.editingNote.BNoteID === 0) {
      this.noteService.createNote(this.editingNote);
    } else {
      this.noteService.updateNote(this.editingNote);
    }

    this.cancelEdit();
  }

  deleteNote(note: BinderNote) {
    if (confirm(`Delete note: "${note.Subject}"?`)) {
      this.noteService.deleteNote(note.BNoteID);
    }
  }
}
